import { persianDaysInMonth } from "../../../shared/utils/persianDate";
import {
  gregorianDaysInMonth,
  normalizeCalendarType,
  round2,
} from "../../../shared/utils/calendarType";

export function normalizeTargetList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export function targetMutationAllowed({
  action,
  canCreate = false,
  canEdit = false,
  canDelete = false,
  periodAllowed = false,
} = {}) {
  if (!periodAllowed) return false;
  if (action === "create") return Boolean(canCreate);
  if (action === "edit") return Boolean(canEdit);
  if (action === "delete") return Boolean(canDelete);
  return false;
}

export function shouldApplySuggestedTargets({
  canFetchSuggested = false,
  hasSuggestion = false,
  isEditMode = false,
  userEditedAmounts = false,
} = {}) {
  return Boolean(
    canFetchSuggested && hasSuggestion && !isEditMode && !userEditedAmounts,
  );
}

export function normalizeSuggestedTargets(data) {
  const payload = data?.data || data || {};

  return {
    sale_target: payload.sale_target ?? payload.avg_sale ?? "",
    customer_target: payload.customer_target ?? payload.avg_customer ?? "",
    f_sale_target: payload.f_sale_target ?? payload.sale_target ?? payload.avg_sale ?? "",
    f_customer_target:
      payload.f_customer_target ?? payload.customer_target ?? payload.avg_customer ?? "",
    formula_used: payload.formula_used || "",
    sale_formula: payload.sale_formula || "",
    customer_formula: payload.customer_formula || "",
    tip: payload.tip || null,
    tip_formula: payload.tip_formula || payload.sale_formula || "",
    metric_unit: payload.metric_unit || "",
    unit_type: payload.unit_type || "",
    months_lookback: payload.months_lookback || 3,
    subject_level: payload.subject_level ?? payload.context?.subject_level ?? null,
    context: payload.context || {},
  };
}

export function generateDateRange(year, month, calendarType = "jalali") {
  if (!year || !month) return { from_date: "", to_date: "" };

  const lastDay =
    calendarType === "gregorian"
      ? gregorianDaysInMonth(year, month)
      : persianDaysInMonth(year, month);
  const from_date = `${year}/${String(month).padStart(2, "0")}/01`;
  const to_date = `${year}/${String(month).padStart(2, "0")}/${String(lastDay).padStart(2, "0")}`;

  return { from_date, to_date };
}

export function generateTargetUid(personnelId, manufacturerId, productGroupId, fromDate, toDate) {
  return `${personnelId}-${manufacturerId}-${productGroupId}-${fromDate.replace(/\//g, "")}-${toDate.replace(/\//g, "")}`;
}

export function buildPersonnelTargetPayload({
  uid,
  personnel_id,
  manufacturer_id,
  product_group_id,
  unit_type,
  sale_target,
  customer_target,
  f_sale_target,
  f_customer_target,
  from_date,
  to_date,
  year_number,
  month_number,
  dc_id,
  level_type,
  saleoffice_id,
  supervisor_id,
}) {
  return {
    uid,
    personnel_id,
    manufacturer_id: parseInt(manufacturer_id, 10),
    product_group_id: parseInt(product_group_id, 10),
    unit_type: unit_type || null,
    sale_target: round2(sale_target),
    customer_target: round2(customer_target),
    f_sale_target: round2(f_sale_target),
    f_customer_target: round2(f_customer_target),
    from_date,
    to_date,
    year_number: parseInt(year_number, 10),
    month_number: parseInt(month_number, 10),
    level_type: level_type != null ? parseInt(level_type, 10) : null,
    dc_id: dc_id || null,
    saleoffice_id: saleoffice_id != null ? parseInt(saleoffice_id, 10) : null,
    supervisor_id: supervisor_id != null ? parseInt(supervisor_id, 10) : null,
    updated_by: 1,
  };
}

export function mapTargetGroupsForStaff(groups, selectedStaff) {
  const productIds =
    selectedStaff?.product_ids || selectedStaff?.product_group_ids || [];

  const filteredGroups =
    productIds.length > 0
      ? groups.filter((group) => productIds.includes(group.id))
      : groups;

  return filteredGroups.map((group) => ({
    id: group.id,
    name: group.name,
    manufacturer_id: group.manufacturer_id,
    product_group_id: group.product_group_id,
    unit: group.unit,
    calendar_type: normalizeCalendarType(group.calendar_type),
  }));
}
